<template>
  <div class="store-search-panel">
    <fn-loading-overlay v-if="loading" />

    <div class="ssp-header">
      <fn-form-store-search
        v-model:query="query"
        :search-payload-initial="searchPayloadInitial"
        :errors="errors"
        :placeholder="$t('storeListing.search.placeholder')"
        single-section
        @submit="onSubmit"
      />

      <fn-button
        class="ssp-locate"
        icon="locator"
        quaternary-alt
        :aria-label="$t('accessibility.storefinder.searchLocateMe')"
        @click="locateUser"
      />

      <fn-button
        :disabled="!filterProperties.length"
        class="ssp-filter"
        icon="filter"
        quaternary-alt
        :aria-label="$t('accessibility.storefinder.searchFilter')"
        @click="openFilterModal"
      />
    </div>
    <div
      v-if="(appliedFilters && appliedFilters.length) || radius !== 20000"
      class="ssp-facets"
    >
      <template v-for="filterGroup of appliedFilters">
        <template v-if="filterGroup.property === 'size'">
          <fn-items-list-item
            :key="filterGroup.property"
            background-color="grey-lighter"
            icon-color="grey-dark"
            font-size="s"
            font-bold
            thin
            @delete="deleteAppliedFilter(filterGroup.property, filterGroup.values)"
          >
            {{ getFilterValueName(filterGroup.property, filterGroup.values) }}
          </fn-items-list-item>
        </template>
        <template v-else>
          <fn-items-list-item
            v-for="item of filterGroup.values"
            :key="`${filterGroup.property}:${item}`"
            background-color="grey-lighter"
            icon-color="grey-dark"
            font-size="s"
            font-bold
            thin
            @delete="deleteAppliedFilter(filterGroup.property, item)"
          >
            {{ getFilterValueName(filterGroup.property, item) }}
          </fn-items-list-item>
        </template>
      </template>
      <fn-items-list-item
        v-if="radius !== 20000"
        background-color="grey-lighter"
        icon-color="grey-dark"
        font-size="s"
        font-bold
        thin
        @delete="clearRadius"
      >
        {{ $t('storeListing.filter.appliedFilters.radius', { radius: radius / 1000 }) }}
      </fn-items-list-item>
    </div>

    <client-only>
      <teleport
        to="#teleports"
        :disabled="!filterModalOpen"
      >
        <fn-modal-store-listing-filter
          v-if="filterModalOpen"
          :filter-properties="filterProperties"
          :pre-applied-filters="appliedFilters"
          :radius="radius"
          @apply="applyFilters"
          @close="closeFilterModal"
        />
      </teleport>
    </client-only>
  </div>
</template>

<script lang="ts">
type PropFilterProperties = Array<{
  property: string;

  values: Array<{
    name: string;
    code: string;
    selected: boolean;
  }>;
}>;

type AppliedFilters = Array<{
  property: string;
  values: Array<string>;
}>;

export default defineComponent({
  props: {
    filterProperties: {
      type: Array as PropType<PropFilterProperties>,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    searchPayloadInitial: {
      type: String,
      default: undefined,
    },
  },
  emits: ['search', 'locateMe'],
  data: () => ({
    query: '',
    filterModalOpen: false,
    appliedFilters: [] as AppliedFilters,
    radius: 20000,
  }),
  watch: {
    filterProperties() {
      this.setAppliedFilters();
    },
  },
  methods: {
    onSubmit() {
      this.resetAppliedFilters();
      this.resetRadius();
      this.handleSearch();
    },

    openFilterModal() {
      this.filterModalOpen = true;
    },

    closeFilterModal() {
      this.filterModalOpen = false;
    },

    applyFilters({ appliedFilters, radius }: { appliedFilters: AppliedFilters; radius: number }) {
      this.appliedFilters = appliedFilters;
      this.radius = radius;
      this.closeFilterModal();
      this.handleSearch();
    },

    handleSearch() {
      this.$emit('search', {
        query: this.query,
        filterProperties: this.appliedFilters,
        radius: this.radius,
      });
    },

    locateUser() {
      this.query = '';
      this.resetRadius();
      this.resetAppliedFilters();
      this.$emit('locateMe');
    },

    setAppliedFilters() {
      if (!this.filterProperties) return;

      this.resetAppliedFilters();

      this.filterProperties.forEach((item) => {
        const values: Array<string> = [];

        item.values.forEach((value) => {
          if (value.selected) {
            values.push(value.code);
          }
        });

        if (values.length) {
          this.appliedFilters.push({
            property: item.property,
            values,
          });
        }
      });
    },

    deleteAppliedFilter(group: string, itemCode: string) {
      const propertyIndex = this.appliedFilters.findIndex((item) => item.property === group);

      let propertyValues = this.appliedFilters[propertyIndex].values;

      if (group === 'size') {
        propertyValues = [];
      } else {
        propertyValues.splice(propertyValues.indexOf(itemCode), 1);
      }

      if (!propertyValues.length) {
        this.appliedFilters.splice(propertyIndex, 1);
      } else {
        this.appliedFilters[propertyIndex].values = propertyValues;
      }

      this.handleSearch();
    },

    getFilterValueName(group: string, item: string | Array<string>) {
      if (group === 'onlySalon') {
        return this.$t('storeListing.filter.appliedFilters.salon');
      }

      if (group === 'onlyActivet') {
        return this.$t('storeListing.filter.appliedFilters.activet');
      }

      if (group === 'size') {
        const storeSize = item.map((size) => size.toUpperCase());
        return this.$t('storeListing.filter.appliedFilters.storeSize', { storeSize: storeSize.join(', ') });
      }

      if (group === 'status') {
        return this.$t(`storeListing.filter.groups.status.options.${item}`);
      }

      const filterProperty = this.filterProperties.find((property) => property.property === group);

      const filterValue = filterProperty?.values.find((value) => value.code === item);

      return filterValue?.name;
    },

    clearRadius() {
      this.resetRadius();
      this.handleSearch();
    },

    resetAppliedFilters() {
      this.appliedFilters = [];
    },

    resetRadius() {
      this.radius = 20000;
    },
  },
});
</script>

<style lang="scss" scoped>
.store-search-panel {
  border-radius: map-get($border-radiuses, 'xl');
  padding: 0 math.div($grid-margin-width, 2) $pad;
  background-color: $color-general-background-neutral-default;

  @include bp(m) {
    padding: $pad;
  }

  :deep(.p-main) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .ssp-header {
    display: flex;
    align-items: flex-start;
    column-gap: math.div($pad, 2);

    :deep(.form-store-search) {
      flex-grow: 1;

      .form-input input {
        border-radius: map-get($border-radiuses, 'l');
      }
    }
  }

  .ssp-facets {
    display: flex;
    flex-wrap: wrap;
    gap: math.div($pad, 2);
    margin-top: $pad;
  }
}
</style>
